import { useReducer, useCallback } from "react";
import moment from "moment";
import PriceAuditContext from "./context";
import PriceAuditReducer, {
  initialState,
  LOADING,
  SET_LOGS,
  SET_STATISTICS,
  SET_QUERY,
  RESET,
} from "./reducer";
import EndPoints from "../../services/end-points";
import { IPriceAuditQuery } from "../../models/price-audit/query";
import { execute } from "../../utils/helpers/execute";

interface IProps {
  children: React.ReactNode;
}

const PriceAuditContextProvider: React.FC<IProps> = ({ children }) => {
  const [state, dispatch] = useReducer(PriceAuditReducer, initialState);

  const setLoading = (loading: string[]) => {
    dispatch({ type: LOADING, payload: loading });
  };

  const getLogs = useCallback(async () => {
    await execute({
      callback: async () => {
        setLoading([...state.loading, "logs"]);
        const response = await EndPoints.priceAudit.getLogs(state.query);
        dispatch({ type: SET_LOGS, payload: response.data });
      },
      fallback: (error) => {
        console.error("Failed to fetch price audit logs:", error);
      },
      finallyCallback: () => {
        setLoading(state.loading.filter((l) => l !== "logs"));
      },
      throwException: false,
    });
  }, [state.loading, state.query]);

  const getStatistics = useCallback(async () => {
    await execute({
      callback: async () => {
        setLoading([...state.loading, "statistics"]);
        const response = await EndPoints.priceAudit.getStatistics({
          from_date: state.query.from_date,
          to_date: state.query.to_date,
        });
        dispatch({ type: SET_STATISTICS, payload: response.data });
      },
      fallback: (error) => {
        console.error("Failed to fetch price audit statistics:", error);
      },
      finallyCallback: () => {
        setLoading(state.loading.filter((l) => l !== "statistics"));
      },
      throwException: false,
    });
  }, [state.loading, state.query]);

  const setQuery = useCallback((query: IPriceAuditQuery) => {
    dispatch({ type: SET_QUERY, payload: query });
  }, []);

  const exportExcel = useCallback(async () => {
    await execute({
      callback: async () => {
        setLoading([...state.loading, "export"]);
        const response = await EndPoints.priceAudit.exportExcel(state.query);
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `Price-Audit-${moment().format("yyyy-MM-DD")}.xlsx`
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      },
      fallback: (error) => {
        console.error("Failed to export price audit:", error);
      },
      finallyCallback: () => {
        setLoading(state.loading.filter((l) => l !== "export"));
      },
      throwException: false,
    });
  }, [state.loading, state.query]);

  const reset = useCallback(() => {
    dispatch({ type: RESET });
  }, []);

  return (
    <PriceAuditContext.Provider
      value={{
        ...state,
        actions: {
          getLogs,
          getStatistics,
          setQuery,
          exportExcel,
          reset,
        },
      }}
    >
      {children}
    </PriceAuditContext.Provider>
  );
};

export default PriceAuditContextProvider;
